import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogType?: 'website' | 'article';
  articlePublishedTime?: string;
  noIndex?: boolean;
}

const SEOHead = ({
  title,
  description,
  keywords,
  canonicalPath = '',
  ogImage = 'https://hostcall.ai/HostCall-ai-logo-v3-transp.png',
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogType = 'website',
  articlePublishedTime,
  noIndex = false,
}: SEOHeadProps) => {
  const { language } = useLanguage();
  const baseUrl = 'https://hostcall.ai';
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  
  const locale = language === 'sv' ? 'sv_SE' : 'en_US';
  const alternateLocale = language === 'sv' ? 'en_US' : 'sv_SE';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="HostCall.ai" />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}
      
      {/* Language */}
      <html lang={language} />
      <meta httpEquiv="content-language" content={language} />
      
      {/* Hreflang Tags for International SEO */}
      <link rel="alternate" hrefLang="en" href={`${baseUrl}${canonicalPath}`} />
      <link rel="alternate" hrefLang="sv" href={`${baseUrl}${canonicalPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${canonicalPath}`} />
      
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="HostCall.ai" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@hostcallai" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Article specific */}
      {ogType === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {ogType === 'article' && (
        <meta property="article:publisher" content="https://hostcall.ai" />
      )}
    </Helmet>
  );
};

export default SEOHead;
